import React from 'react';

// The correct Hearthstone engine order for dual-tribes
// Internal mappings applied: MECHANICAL -> Mech, PET -> Beast
const TRIBE_ORDER = [
  'Undead',
  'Elemental',
  'Mech',
  'Demon',
  'Murloc',
  'Quilboar',
  'Naga',
  'Pet',
  'Dragon',
  'Totem',
  'Pirate'
];

// Minion tribes with image paths
const MINION_TRIBES = [
  { name: 'All', icon: '/images/tribes/all.webp' },
  { name: 'Beast', icon: '/images/tribes/beast.webp' },
  { name: 'Demon', icon: '/images/tribes/demon.webp' },
  { name: 'Draenei', icon: '/images/tribes/draenei.webp' },
  { name: 'Dragon', icon: '/images/tribes/dragon.webp' },
  { name: 'Elemental', icon: '/images/tribes/elemental.webp' },
  { name: 'Mech', icon: '/images/tribes/mech.webp' },
  { name: 'Murloc', icon: '/images/tribes/murloc.webp' },
  { name: 'Naga', icon: '/images/tribes/naga.webp' },
  { name: 'Pirate', icon: '/images/tribes/pirate.webp' },
  { name: 'Quilboar', icon: '/images/tribes/quilboar.webp' },
  { name: 'Totem', icon: '/images/tribes/totem2.webp' },
  { name: 'Undead', icon: '/images/tribes/undead.webp' }
];

// Spell schools with image paths
const SPELL_SCHOOLS = [
  { name: 'Arcane', icon: '/images/schools/arcane.webp' },
  { name: 'Fire', icon: '/images/schools/fire.webp' },
  { name: 'Frost', icon: '/images/schools/frost.webp' },
  { name: 'Nature', icon: '/images/schools/nature.webp' },
  { name: 'Holy', icon: '/images/schools/holy.webp' },
  { name: 'Shadow', icon: '/images/schools/shadow.webp' },
  { name: 'Fel', icon: '/images/schools/fel.webp' }
];

export default function TribeModal({ isOpen, onClose, cardParams, setCardParams }) {
  if (!isOpen) return null;

  // Determine if card type is Spell
  const isSpell = cardParams.Type?.toLowerCase() === 'spell';
  const optionsList = isSpell ? SPELL_SCHOOLS : MINION_TRIBES;
  const listTitle = isSpell ? 'Spell Schools' : 'Minion Tribes';

  // Parse currently selected values into an array
  const currentSelections = cardParams.Tribe 
    ? cardParams.Tribe.split(',').map(t => t.trim()).filter(Boolean) 
    : [];

  const handleToggleTribe = (name) => {
    let newSelections = [...currentSelections];
    
    if (newSelections.includes(name)) {
      newSelections = newSelections.filter(t => t !== name);
    } else {
      newSelections.push(name);
      if (newSelections.length > 2) {
        newSelections.shift(); // Keep only the 2 most recent selections
      }
    }

    // Apply strict sorting for minion tribes based on Hearthstone's internal priority
    if (!isSpell) {
      newSelections.sort((a, b) => {
        const indexA = TRIBE_ORDER.indexOf(a);
        const indexB = TRIBE_ORDER.indexOf(b);
        
        // If a tribe isn't in the official order array (like 'Draenei' or 'All'), give it a weight of 999 so it goes to the end
        const weightA = indexA !== -1 ? indexA : 999;
        const weightB = indexB !== -1 ? indexB : 999;
        
        return weightA - weightB;
      });
    }

    setCardParams(prev => ({ ...prev, Tribe: newSelections.join(', ') }));
  };

  const handleClear = () => {
    setCardParams(prev => ({ ...prev, Tribe: '' }));
  };

  return (
    <div 
      className="modal fade show d-block" 
      style={{ backgroundColor: 'rgba(0,0,0,0.7)' }} 
      tabIndex="-1" 
      onClick={onClose}
    >
      <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
        <div className="custom-modal modal-content bg-dark text-light border-secondary shadow-lg">
          
          <div className="modal-header border-secondary">
            <h5 className="modal-title">
              <i className={`fa ${isSpell ? 'fa-book-magic text-info' : 'fa-paw text-warning'} me-2`}></i>
              Select {listTitle}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>

          <div className="modal-body">
            <p className="small text-muted mb-3">You can select up to two options.</p>
            
            <div className="row g-2">
              {optionsList.map(item => {
                const isSelected = currentSelections.includes(item.name);
                const colSize = item.name === 'All' ? 'col-12' : 'col-6';

                return (
                  <div className={colSize} key={item.name}>
                    <button
                      type="button"
                      className={`btn w-100 d-flex align-items-center gap-2 justify-content-start text-start tribe-modal-btn ${
                        isSpell ? 'type-spell' : 'type-minion'
                      } ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleToggleTribe(item.name)}
                    >
                      <img 
                        src={item.icon} 
                        alt={item.name} 
                        className="tribe-btn-icon"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }} 
                      />
                      <span className="tribe-btn-text">{item.name}</span>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="modal-footer border-secondary d-flex justify-content-between">
            <button type="button" className="btn btn-outline-danger" onClick={handleClear}>
              <i className="fa fa-trash me-2"></i>Clear All
            </button>
            <button type="button" className="btn btn-success px-4" onClick={onClose}>
              Done
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}